import { observer } from "mobx-react-lite";
import { incompleteTestRunStatuses, TestRun } from "models/test-run";
import "./logs-tab.scss";
import { TestRunHistoryStore } from "stores";
import { useInitialEffect } from "core/react-utils";
import { Accordion, Icon } from "semantic-ui-react";
import { useState, useCallback } from "react";
import { isBlank, isNotBlank } from "../../core/utils";
import Spinner from "../../components/spinner";
import TerminalOutput from "../../components/terminal-output";
import { useLogStream, formatLogMessage, LogStreamMessage } from "core/use-log-stream";

interface Props {
  testRun: TestRun;
}

const LogsTab = observer(({ testRun }: Props) => {
  const [activeIndex, setActiveIndex] = useState(-1);
  const [loading, setLoading] = useState(false);
  const [streamedContent, setStreamedContent] = useState("");

  const isInProgress = incompleteTestRunStatuses.includes(testRun.status);

  // Handle incoming log messages for streaming
  const handleLogMessage = useCallback((message: LogStreamMessage) => {
    const formattedLine = formatLogMessage(message);
    setStreamedContent((prev) => prev + (prev ? "\n" : "") + formattedLine);
  }, []);

  // Use WebSocket streaming when test run is in progress
  const { isConnected, isConnecting } = useLogStream({
    testRunId: testRun.id,
    enabled: isInProgress,
    onMessage: handleLogMessage,
  });

  useInitialEffect(async () => {
    if (!isInProgress) {
      setLoading(true);
      await TestRunHistoryStore.loadLogs(testRun.id);
      setLoading(false);
    }
  });

  const handleAccordionClick = async (_e, titleProps) => {
    const { index } = titleProps;
    const newIndex = activeIndex === index ? -1 : index;

    setActiveIndex(newIndex);
  };

  // Show streaming view when test run is in progress
  if (isInProgress) {
    return (
      <div className="logs-tab">
        <div className="streaming-logs">
          <TerminalOutput
            content={streamedContent}
            height="calc(100vh - 300px)"
            streaming={true}
            isConnected={isConnected}
            isLoading={isConnecting}
            fillHeight
          />
          {!streamedContent && (
            <div style={{ marginTop: "8px" }}>
              <Spinner text="Waiting for logs..." />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (loading)
    return <Spinner text={"Loading Runner Logs"} />;

  const logItems = TestRunHistoryStore.logItems;
  const accordionData = [];
  let index = 0;

  if (logItems && logItems.length > 0) {
    logItems.forEach(log => {
      if (isNotBlank(log.testListGenerationLog) || isNotBlank(log.runnerSetupLog)) {
        accordionData.push(
          <div key={`container-${index}`}>
            <Accordion.Title index={index} key={`test-run-log-title-${index}`} active={activeIndex === index}
                             onClick={handleAccordionClick}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <div>
                  {activeIndex === index && <Icon name={"caret down"} />}
                  {activeIndex !== index && <Icon name={"caret right"} />}
                  Runner: {log.ecsTaskArn}
                </div>
                <div>
                  Test List Generation Log:
                  {isNotBlank(log.testListGenerationLog) && <Icon style={{ marginLeft: "6px" }} name={"checkmark"} />}
                  {isBlank(log.testListGenerationLog) && <Icon style={{ marginLeft: "6px" }} name={"x"} />}
                </div>
              </div>
            </Accordion.Title>

            <Accordion.Content key={`test-run-log-content-${index}`} active={activeIndex === index}>
              {isNotBlank(log.runnerSetupLog) && (
                <>
                  <h4 key={`header-setup-${index}`}>Runner Setup Log</h4>
                  <TerminalOutput content={log.runnerSetupLog} height="300px" />
                </>
              )}
              {isNotBlank(log.testListGenerationLog) && (
                <>
                  <h4 key={`header-testlist-${index}`} style={{ marginTop: isNotBlank(log.runnerSetupLog) ? "16px" : 0 }}>
                    Test List Generation Log
                  </h4>
                  <TerminalOutput content={log.testListGenerationLog} height="300px" />
                </>
              )}
            </Accordion.Content>
          </div>,
        );

        index++;
      }
    });
  }

  return (
    <div className="logs-tab">
      <Accordion fluid styled>
        {accordionData}
      </Accordion>
    </div>
  );
});

export default LogsTab;
